// src/views/InformationPublications/InformationPublicationDetail.tsx

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  Modal,
  TextField,
  Fade,
  Select,
  MenuItem,
  OutlinedInput,
  ListItemText,
  FormControl, // Added
  InputLabel, // Added
  Checkbox // Added
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CreateIcon from '@mui/icons-material/Create';
import UpdateIcon from '@mui/icons-material/Update';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Adiciona useLocation
import Lottie from 'react-lottie'; // Adiciona Lottie
import AIAnimation from '../../assets/lottie/ai-v2.json'; // Adiciona animação
import swal from 'sweetalert';

import { useEntity } from '../../context/EntityContext';
import {
  getInformationPublication,
  buildPublicInformationPdfUrl,
  updateInformationPublicationDetails,
  listCategories, // Trocado
  updateInformationPublicationCategories,
  ICategory // Importa o tipo
} from '../../services/informationPublicationsService';

function formatDate(dateString?: string): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'long',
      timeStyle: 'short'
    }).format(date);
  } catch {
    return dateString;
  }
}

// cores para chips de categorias
const catColors = ['primary','secondary','success','error','warning','info'] as const;

// Função auxiliar para achatar a árvore de categorias para o Select
const flattenCategoriesForSelect = (categories: ICategory[], level = 0): { id: string; label: string }[] => {
  let flatList: { id: string; label: string }[] = [];
  const prefix = '↳ '.repeat(level);

  for (const category of categories) {
    flatList.push({ id: category.id, label: `${prefix}${category.category}` });
    if (category.children && category.children.length > 0) {
      flatList = flatList.concat(flattenCategoriesForSelect(category.children, level + 1));
    }
  }
  return flatList;
};

const InformationPublicationDetail: React.FC = () => {
  const { informationId } = useParams();
  const { selectedEntity } = useEntity();
  const navigate = useNavigate();
  const location = useLocation(); // Hook para acessar o state da navegação

  // Verifica se a animação deve ser exibida
  const shouldAnimate = location.state?.showAnimation;

  // Estados para a animação em cascata
  const [showAnimation, setShowAnimation] = useState(shouldAnimate);
  const [showTitle, setShowTitle] = useState(!shouldAnimate);
  const [showSummary, setShowSummary] = useState(!shouldAnimate);
  const [showCategories, setShowCategories] = useState(!shouldAnimate);
  const [showButtons, setShowButtons] = useState(!shouldAnimate);
  
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<any | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedInfo, setEditedInfo] = useState<any | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [allCategories, setAllCategories] = useState<ICategory[]>([]); // Tipo atualizado
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('');
  const [selectedSecondaryCategories, setSelectedSecondaryCategories] = useState<string[]>([]);

  const fetchDetails = async () => {
    if (!informationId || !selectedEntity) {
      setLoading(false);
      return;
    }
    try {
      const [detailsData, categoriesData] = await Promise.all([
        getInformationPublication(informationId, selectedEntity),
        listCategories(selectedEntity) // Função atualizada
      ]);
      setInfo(detailsData);
      setAllCategories(categoriesData);

      // Inicializa os estados das categorias
      if (detailsData?.main_category) {
        setSelectedMainCategory(detailsData.main_category.id);
      }
      if (detailsData?.categories) {
        setSelectedSecondaryCategories(detailsData.categories.map((c: any) => c.id));
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes ou categorias:', error);
      swal('Erro', 'Não foi possível carregar os dados.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [informationId, selectedEntity]);

  // Lottie (animação)
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: AIAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  // Efeito para controlar a animação em cascata
  useEffect(() => {
    if (shouldAnimate) {
      const timer1 = setTimeout(() => setShowTitle(true), 1000);
      const timer2 = setTimeout(() => setShowSummary(true), 2000);
      const timer3 = setTimeout(() => setShowCategories(true), 3000);
      const timer4 = setTimeout(() => setShowButtons(true), 4000);
      // Opcional: esconder a animação lottie após a conclusão
      const timer5 = setTimeout(() => setShowAnimation(false), 1000); // Esconde mais rápido

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
        clearTimeout(timer5);
      };
    }
  }, [shouldAnimate]);

  const handleSaveChanges = async () => {
    if (!editedInfo) return;

    try {
      await updateInformationPublicationDetails(
        editedInfo.id,
        editedInfo.author,
        editedInfo.document_date
      );
      swal('Sucesso', 'Informação atualizada com sucesso.', 'success');
      setIsEditModalOpen(false);
      fetchDetails(); // Recarrega os detalhes
    } catch (error) {
      console.error('Erro ao salvar:', error);
      swal('Erro', 'Não foi possível salvar as alterações.', 'error');
    }
  };

  const handleSaveCategories = async () => {
    if (!info || !selectedMainCategory) {
      swal('Atenção', 'Você deve selecionar uma categoria principal.', 'warning');
      return;
    }
    try {
      await updateInformationPublicationCategories(
        info.id,
        selectedMainCategory,
        selectedSecondaryCategories
      );
      swal('Sucesso', 'Categorias atualizadas com sucesso.', 'success');
      setIsCategoryModalOpen(false);
      fetchDetails();
    } catch (error) {
      console.error('Erro ao salvar categorias:', error);
      swal('Erro', 'Não foi possível salvar as categorias.', 'error');
    }
  };

  const handleOpenEditModal = () => {
    setEditedInfo({ ...info }); // Popula o estado de edição com os dados atuais
    setIsEditModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    // Reverte as categorias para o estado original ao fechar
    if (info?.main_category) {
      setSelectedMainCategory(info.main_category.id);
    }
    if (info?.categories) {
      setSelectedSecondaryCategories(info.categories.map((c: any) => c.id));
    }
    setIsCategoryModalOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!info) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="body1">
          Informação não encontrada ou você não tem acesso.
        </Typography>
      </Box>
    );
  }

  const createdAt = formatDate(info.created_at);
  const updatedAt = formatDate(info.updated_at);
  const catList = info.categories || [];
  const mainCategory = info.main_category;

  /**
   * (1.1/1.2) Usa a rota pública do PDF, independente de `uploaded_filename`.
   */
  const handleOpenFile = () => {
    const url = buildPublicInformationPdfUrl(info.id);
    window.open(url, '_blank');
  };

  const handleCopyLink = async () => {
    const url = buildPublicInformationPdfUrl(info.id);
    try {
      await navigator.clipboard.writeText(url);
      swal('Sucesso', 'Link copiado para a área de transferência.', 'success');
    } catch (err) {
      console.error('Erro ao copiar link:', err);
      swal('Erro', 'Não foi possível copiar o link.', 'error');
    }
  };

  // Editar categorias
  const handleEditCategories = () => {
    setIsCategoryModalOpen(true);
  };

  return (
    <Box sx={{ mt: 3 }}>
      {showAnimation && (
        <Box sx={{ width: 180, mb: 2, alignSelf: 'center', margin: 'auto' }}>
          <Lottie options={defaultOptions} />
        </Box>
      )}
      
      <Fade in={showTitle} timeout={600}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {info.title || 'Sem título'}
        </Typography>
      </Fade>

      {/* Envolve o resto do conteúdo com Fade */}
      <Fade in={showSummary} timeout={600}>
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {info.author && (
              <Chip
                icon={<PersonIcon />}
                label={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    {`Autor: ${info.author}`}
                    <Typography component="span" sx={{ ml: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                      Inferido&nbsp;
                      <Tooltip title="Esta informação foi originalmente inferida por um modelo de linguagem e pode ser editada caso necessário." arrow>
                        <Typography component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', fontSize: '0.7rem' }}>
                          (o que é isso?)
                        </Typography>
                      </Tooltip>
                    </Typography>
                  </Box>
                }
                variant="outlined"
                color="info"
              />
            )}
            {info.document_date && (
              <Chip
                icon={<CalendarMonthIcon />}
                label={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    {`Data do Documento: ${new Date(info.document_date).toLocaleString('pt-BR', { timeZone: 'UTC' })}`}
                    <Typography component="span" sx={{ ml: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                      Inferido&nbsp;
                      <Tooltip title="Esta informação foi originalmente inferida por um modelo de linguagem e pode ser editada caso necessário." arrow>
                        <Typography component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', fontSize: '0.7rem' }}>
                          (o que é isso?)
                        </Typography>
                      </Tooltip>
                    </Typography>
                  </Box>
                }
                variant="outlined"
                color="info"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Chip
              icon={<CreateIcon />}
              label={`Criado em: ${createdAt}`}
              variant="outlined"
            />
            <Chip
              icon={<UpdateIcon />}
              label={`Última atualização: ${updatedAt}`}
              variant="outlined"
            />
          </Box>

          <Typography variant="body1" sx={{ mb: 3 }}>
            {info.summary || 'Sem resumo'}
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 'bold' }}>
            Categoria Principal
          </Typography>
          {mainCategory ? (
            <Chip
              label={mainCategory.category}
              color="primary"
              sx={{ mb: 3 }}
            />
          ) : (
            <Typography variant="body2" sx={{ mb: 3 }}>Nenhuma categoria principal definida.</Typography>
          )}

          {catList.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 'bold' }}>
                Categorias Secundárias
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {catList.map((cat: any, idx: number) => (
                  <Chip
                    key={cat.id}
                    label={cat.category}
                    color={catColors[idx % catColors.length]}
                    variant="outlined"
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Fade>

      <Fade in={showCategories} timeout={600}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Tooltip title="Abrir o arquivo PDF (rota pública)">
            <Button variant="outlined" onClick={handleOpenFile}>
              Abrir arquivo
            </Button>
          </Tooltip>
          <Tooltip title="Copiar link de acesso (rota pública)">
            <Button variant="outlined" onClick={handleCopyLink}>
              Copiar link
            </Button>
          </Tooltip>
        </Box>
      </Fade>
      
      <Fade in={showButtons} timeout={600}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleEditCategories}>
            Editar Categorias
          </Button>
          <Button variant="contained" color="primary" onClick={handleOpenEditModal}>
            Editar Informações
          </Button>
        </Box>
      </Fade>

      {/* Modal de Edição */}
      <Modal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        closeAfterTransition
      >
        <Fade in={isEditModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" component="h2">
              Editar Informações
            </Typography>
            {editedInfo && (
              <>
                <TextField
                  fullWidth
                  label="Autor"
                  value={editedInfo.author || ''}
                  onChange={(e) => setEditedInfo({ ...editedInfo, author: e.target.value })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Data do Documento"
                  type="date"
                  value={editedInfo.document_date ? editedInfo.document_date.split('T')[0] : ''}
                  onChange={(e) => setEditedInfo({ ...editedInfo, document_date: e.target.value })}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setIsEditModalOpen(false)}>Cancelar</Button>
                  <Button onClick={handleSaveChanges} variant="contained" sx={{ ml: 2 }}>
                    Salvar Alterações
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Modal de Edição de Categorias */}
      <Modal
        open={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        closeAfterTransition
      >
        <Fade in={isCategoryModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              Editar Categorias
            </Typography>

            {/* Seletor Categoria Principal */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="main-category-select-label">Categoria Principal *</InputLabel>
              <Select
                labelId="main-category-select-label"
                value={selectedMainCategory}
                onChange={(e) => setSelectedMainCategory(e.target.value as string)}
                label="Categoria Principal *"
              >
                {flattenCategoriesForSelect(allCategories).map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Seletor Categorias Secundárias */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="secondary-categories-select-label">Categorias Secundárias</InputLabel>
              <Select
                labelId="secondary-categories-select-label"
                multiple
                value={selectedSecondaryCategories}
                onChange={(e) => setSelectedSecondaryCategories(e.target.value as string[])}
                input={<OutlinedInput label="Categorias Secundárias" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((id) => (
                      <Chip key={id} label={flattenCategoriesForSelect(allCategories).find(c => c.id === id)?.label || id} />
                    ))}
                  </Box>
                )}
              >
                {flattenCategoriesForSelect(allCategories)
                  .filter(cat => cat.id !== selectedMainCategory) // Não pode ser a principal
                  .map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Checkbox checked={selectedSecondaryCategories.indexOf(cat.id) > -1} />
                    <ListItemText primary={cat.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseCategoryModal}>Cancelar</Button>
              <Button onClick={handleSaveCategories} variant="contained" sx={{ ml: 2 }}>
                Salvar Categorias
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

    </Box>
  );
};

export default InformationPublicationDetail;